import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarRange, ChevronDown, Search, X } from 'lucide-react';
import * as React from 'react';
import { CampoData } from '@/components/patterns/date-picker';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isoParaBR } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * FilterBar / FilterField (Playbook § Padrões de casa).
 *
 * Uma barra só, campos alinhados pela base, sem moldura. O rótulo é 12px bold
 * na cor do texto — não cinza caixa-alta — e continua visível (Hard rule 4).
 */

export function FilterBar({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-end gap-4', className)} role="search">
      {children}
    </div>
  );
}

export function FilterBusca({
  id,
  label,
  valor,
  aoMudar,
  placeholder,
  className,
}: {
  id: string;
  label: string;
  valor: string;
  aoMudar: (valor: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <Field id={id} label={label} className={cn('min-w-56 max-w-md flex-1', className)}>
      {(props) => (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
          <Input
            {...props}
            type="search"
            value={valor}
            placeholder={placeholder}
            onChange={(evento) => aoMudar(evento.target.value)}
            className="pl-9"
          />
        </div>
      )}
    </Field>
  );
}

const TODOS = '__todos__';

export function FilterSelect({
  id,
  label,
  valor,
  aoMudar,
  opcoes,
  rotuloTodos = 'Todos',
  className,
}: {
  id: string;
  label: string;
  valor: string | null;
  aoMudar: (valor: string | null) => void;
  opcoes: Array<{ valor: string; rotulo: string }>;
  rotuloTodos?: string;
  className?: string;
}) {
  return (
    <Field id={id} label={label} className={cn('min-w-44', className)}>
      {(props) => (
        <Select value={valor ?? TODOS} onValueChange={(proximo) => aoMudar(proximo === TODOS ? null : proximo)}>
          <SelectTrigger id={props.id} aria-describedby={props['aria-describedby']}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>{rotuloTodos}</SelectItem>
            {opcoes.map((opcao) => (
              <SelectItem key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  );
}

export interface FilterPeriodoPreset {
  valor: string;
  rotulo: string;
}

/** `min` do campo "De" quando há um teto de dias: `limiteDias - 1` dias antes
 * de "Até" — mesma regra de truncamento "pelo início" que o backend aplica se
 * o intervalo pedido for maior que o teto. Sem "Até" ainda escolhido não há o
 * que limitar aqui (quem passa a valer é o `max` do outro campo, relativo a
 * "De") — âncoras em "hoje" foram removidas de propósito: elas prendiam "De"
 * aos últimos `limiteDias` dias a partir de agora mesmo com "Até" vazio,
 * rejeitando (em silêncio) qualquer data mais antiga antes que "Até" tivesse
 * chance de existir. */
function minimoDe(ate: string, limiteDias: number | undefined): string | undefined {
  if (!limiteDias || !ate) return undefined;
  const [ano, mes, dia] = ate.split('-').map(Number);
  const data = new Date(ano, (mes ?? 1) - 1, dia ?? 1);
  data.setDate(data.getDate() - (limiteDias - 1));
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
}

/** Espelho de `minimoDe`: `max` do campo "Até" quando há um teto de dias —
 * `limiteDias - 1` dias depois de "De". Sem "De" ainda escolhido não há o que
 * limitar aqui (quem passa a valer é o `min` do outro campo, relativo a "Até"). */
function maximoAte(de: string, limiteDias: number | undefined): string | undefined {
  if (!limiteDias || !de) return undefined;
  const [ano, mes, dia] = de.split('-').map(Number);
  const data = new Date(ano, (mes ?? 1) - 1, dia ?? 1);
  data.setDate(data.getDate() + (limiteDias - 1));
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
}

function rotuloPeriodo(
  periodo: string | null,
  de: string,
  ate: string,
  presets: FilterPeriodoPreset[],
  rotuloTodos: string,
): string {
  if (periodo === 'custom') {
    if (de && ate) return `${isoParaBR(de)} – ${isoParaBR(ate)}`;
    if (de) return `A partir de ${isoParaBR(de)}`;
    if (ate) return `Até ${isoParaBR(ate)}`;
    return 'Personalizado';
  }
  return presets.find((item) => item.valor === periodo)?.rotulo ?? rotuloTodos;
}

/**
 * Seletor de período num popover só (padrão Grafana: intervalo absoluto à
 * esquerda, atalhos rápidos à direita) — em vez de "De"/"Até" soltos na barra
 * de filtros, que estouram pra linha de baixo em telas estreitas.
 */
export function FilterPeriodo({
  id,
  label = 'Período',
  periodo,
  de,
  ate,
  aoMudar,
  presets,
  rotuloTodos = 'Qualquer período',
  limiteDias,
  className,
}: {
  id: string;
  label?: string;
  /** Preset ativo (`presets[].valor`), `'custom'` para intervalo absoluto, ou `null` = qualquer período. */
  periodo: string | null;
  /** ISO civil — só relevante quando `periodo === 'custom'`. */
  de: string;
  ate: string;
  aoMudar: (valor: { periodo: string | null; de: string; ate: string }) => void;
  presets: FilterPeriodoPreset[];
  rotuloTodos?: string;
  /** Teto de dias do intervalo personalizado (ex.: Histórico = 31) — o campo
   * "De" fica bloqueado antes disso e um valor digitado mais antigo é
   * truncado ao aplicar. Sem isto, intervalo livre (comportamento de hoje). */
  limiteDias?: number;
  className?: string;
}) {
  const [aberto, setAberto] = React.useState(false);
  const [rascunhoDe, setRascunhoDe] = React.useState(de);
  const [rascunhoAte, setRascunhoAte] = React.useState(ate);

  function alternarAberto(valor: boolean) {
    setAberto(valor);
    // Rascunho só é reiniciado ao ABRIR — fechar sem aplicar não deve perder
    // o que a pessoa já tinha digitado se reabrir em seguida.
    if (valor) {
      setRascunhoDe(de);
      setRascunhoAte(ate);
    }
  }

  function escolherPreset(valor: string | null) {
    aoMudar({ periodo: valor, de: '', ate: '' });
    setAberto(false);
  }

  function aplicarPersonalizado() {
    // Defesa a mais além da validação ao vivo dos dois `CampoData` (que já
    // bloqueia digitar/clicar fora do teto e um intervalo invertido): cobre
    // digitar nos dois campos rápido, sem sair de "De" (blur) antes de mexer
    // em "Até" — aí o `min`/`max` que "Até" recebeu durante a digitação ainda
    // era o de ANTES do novo "De". "De" trunca primeiro, "Até" depois — na
    // mesma ordem que o backend usa.
    const minimo = minimoDe(rascunhoAte, limiteDias);
    let deFinal = minimo && rascunhoDe && rascunhoDe < minimo ? minimo : rascunhoDe;
    if (rascunhoAte && deFinal && deFinal > rascunhoAte) deFinal = rascunhoAte; // "De" nunca depois de "Até"

    const maximo = maximoAte(deFinal, limiteDias);
    let ateFinal = maximo && rascunhoAte && rascunhoAte > maximo ? maximo : rascunhoAte;
    if (deFinal && ateFinal && ateFinal < deFinal) ateFinal = deFinal; // "Até" nunca antes de "De"

    aoMudar({ periodo: 'custom', de: deFinal, ate: ateFinal });
    setAberto(false);
  }

  return (
    <Field id={id} label={label} className={cn('min-w-48', className)}>
      {(props) => (
        <PopoverPrimitive.Root open={aberto} onOpenChange={alternarAberto}>
          <PopoverPrimitive.Trigger asChild>
            <button
              id={props.id}
              type="button"
              aria-describedby={props['aria-describedby']}
              className="flex h-10 w-full items-center gap-2 rounded-input border border-border-strong bg-bg-surface px-3 text-body text-on-surface transition-colors duration-fast ease-standard hover:border-primary"
            >
              <CalendarRange className="size-4 shrink-0 text-icon" aria-hidden="true" />
              <span className="flex-1 truncate text-left">{rotuloPeriodo(periodo, de, ate, presets, rotuloTodos)}</span>
              <ChevronDown className="size-4 shrink-0 text-icon" aria-hidden="true" />
            </button>
          </PopoverPrimitive.Trigger>

          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              // z-[70]: precisa ficar acima de diálogos aninhados (Auditoria
              // usa z-[60] pra empilhar sobre a Administração já aberta) —
              // como Popover renderiza num portal próprio, o z-50 padrão
              // ficava ATRÁS do diálogo que o contém, interceptando cliques.
              className="z-[70] flex overflow-hidden rounded-input border border-border bg-bg-surface shadow-md duration-base ease-standard data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
            >
              <div className="flex w-52 flex-col gap-3 border-r border-border p-3">
                <span className="text-micro font-bold uppercase tracking-[0.06em] text-on-surface-muted">
                  Intervalo personalizado
                </span>
                <Field id={`${id}-de`} label="De">
                  {(campoProps) => (
                    <CampoData
                      {...campoProps}
                      valor={rascunhoDe}
                      aoMudar={setRascunhoDe}
                      min={minimoDe(rascunhoAte, limiteDias)}
                      max={rascunhoAte || undefined}
                    />
                  )}
                </Field>
                <Field id={`${id}-ate`} label="Até">
                  {(campoProps) => (
                    <CampoData
                      {...campoProps}
                      valor={rascunhoAte}
                      aoMudar={setRascunhoAte}
                      min={rascunhoDe || undefined}
                      max={maximoAte(rascunhoDe, limiteDias)}
                    />
                  )}
                </Field>
                {limiteDias ? (
                  <span className="text-micro text-on-surface-muted">Janela de até {limiteDias} dias.</span>
                ) : null}
                <Button size="sm" variant="primary" disabled={!rascunhoDe && !rascunhoAte} onClick={aplicarPersonalizado}>
                  Aplicar intervalo
                </Button>
              </div>

              <div className="flex w-40 flex-col gap-0.5 p-2">
                <button
                  type="button"
                  onClick={() => escolherPreset(null)}
                  className={cn(
                    'rounded-chip px-2.5 py-2 text-left text-body transition-colors duration-fast ease-standard hover:bg-bg-subtle',
                    periodo === null && 'bg-primary-wash font-bold text-primary',
                  )}
                >
                  {rotuloTodos}
                </button>
                {presets.map((preset) => (
                  <button
                    key={preset.valor}
                    type="button"
                    onClick={() => escolherPreset(preset.valor)}
                    className={cn(
                      'rounded-chip px-2.5 py-2 text-left text-body transition-colors duration-fast ease-standard hover:bg-bg-subtle',
                      periodo === preset.valor && 'bg-primary-wash font-bold text-primary',
                    )}
                  >
                    {preset.rotulo}
                  </button>
                ))}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
    </Field>
  );
}

/**
 * Filtros de ANA e Interno, lado a lado — mesmos dois controles em toda
 * listagem de instalações, pra ninguém precisar reaprender o rótulo.
 */
export function FilterFlagsInstalacao({
  idBase,
  ana,
  aoMudarAna,
  interno,
  aoMudarInterno,
  mostrarAna,
  mostrarInterno,
}: {
  idBase: string;
  /** Só faz sentido filtrar por uma flag se alguma instalação a tem. */
  mostrarAna: boolean;
  mostrarInterno: boolean;
  ana: string | null;
  aoMudarAna: (valor: string | null) => void;
  interno: string | null;
  aoMudarInterno: (valor: string | null) => void;
}) {
  const opcoes = [
    { valor: 'sim', rotulo: 'Sim' },
    { valor: 'nao', rotulo: 'Não' },
  ];
  return (
    <>
      {mostrarAna ? (
        <FilterSelect
          id={`${idBase}-ana`}
          label="ANA"
          valor={ana}
          aoMudar={aoMudarAna}
          rotuloTodos="Todas"
          opcoes={opcoes}
          className="min-w-32"
        />
      ) : null}
      {mostrarInterno ? (
        <FilterSelect
          id={`${idBase}-interno`}
          label="Interno"
          valor={interno}
          aoMudar={aoMudarInterno}
          rotuloTodos="Todas"
          opcoes={opcoes}
          className="min-w-32"
        />
      ) : null}
    </>
  );
}

/** Botão de limpar filtros: só aparece quando há filtro ativo. */
export function FilterLimpar({ ativo, aoLimpar, quantidade }: { ativo: boolean; aoLimpar: () => void; quantidade: number }) {
  if (!ativo) return null;
  return (
    <Button variant="ghost" size="sm" onClick={aoLimpar} className="mb-0.5 h-10">
      <X aria-hidden="true" />
      Limpar {quantidade === 1 ? '1 filtro' : `${quantidade} filtros`}
    </Button>
  );
}
